import React, { useState } from 'react';
import { Container, Typography, Box } from '@mui/material';
import { motion } from 'framer-motion';
import GitHubIcon from '@mui/icons-material/GitHub';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 18 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] },
});

const socials = [
  {
    Icon: GitHubIcon,
    label: 'GitHub',
    handle: 'Kihsomray',
    href: 'https://github.com/Kihsomray',
  },
  {
    Icon: LinkedInIcon,
    label: 'LinkedIn',
    handle: 'myarmo',
    href: 'https://linkedin.com/in/myarmo',
  },
];

function SocialRow({ item, index }) {
  const [hovered, setHovered] = useState(false);

  return (
    <motion.div {...fadeUp(0.35 + index * 0.1)}>
      <Box
        component="a"
        href={item.href}
        target="_blank"
        rel="noopener noreferrer"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 2.5,
          textDecoration: 'none',
          borderBottom: '1px solid rgba(255,255,255,0.07)',
          transition: 'all 0.2s ease',
          cursor: 'pointer',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <item.Icon sx={{
            fontSize: '1.1rem',
            color: hovered ? '#F5F5F7' : 'rgba(245,245,247,0.38)',
            transition: 'color 0.2s ease',
          }} />
          <Typography sx={{
            color: hovered ? '#F5F5F7' : 'rgba(245,245,247,0.75)',
            fontWeight: 400,
            fontSize: '1rem',
            letterSpacing: '-0.015em',
            transition: 'color 0.2s ease',
          }}>
            {item.label}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{
            color: hovered ? '#2997FF' : 'rgba(245,245,247,0.28)',
            fontSize: '0.88rem',
            letterSpacing: '-0.01em',
            transition: 'color 0.2s ease',
          }}>
            /{item.handle}
          </Typography>
          <ArrowForwardIcon sx={{
            fontSize: '0.82rem',
            color: hovered ? '#2997FF' : 'rgba(245,245,247,0.18)',
            transform: hovered ? 'translateX(3px)' : 'none',
            transition: 'all 0.2s ease',
          }} />
        </Box>
      </Box>
    </motion.div>
  );
}

export default function Contact() {
  const [emailHovered, setEmailHovered] = useState(false);

  return (
    <Container maxWidth="sm" sx={{ mt: { xs: 10, md: 16 }, mb: 16 }}>

      {/* ── Eyebrow ───────────────────────────────────────────────── */}
      <motion.div {...fadeUp()}>
        <Typography sx={{
          fontSize: '0.72rem',
          fontWeight: 500,
          color: '#2997FF',
          textTransform: 'uppercase',
          letterSpacing: '0.16em',
          mb: 2,
          textAlign: 'center',
        }}>
          Contact
        </Typography>
      </motion.div>

      {/* ── Headline ──────────────────────────────────────────────── */}
      <motion.div {...fadeUp(0.07)}>
        <Typography variant="h2" sx={{
          textAlign: 'center',
          mb: 3,
          fontSize: 'clamp(2rem, 6vw, 3.2rem)',
          letterSpacing: '-0.035em',
          fontWeight: 700,
          lineHeight: 1.1,
        }}>
          Let's build something.
        </Typography>
      </motion.div>

      {/* ── Subhead ───────────────────────────────────────────────── */}
      <motion.div {...fadeUp(0.13)}>
        <Typography sx={{
          textAlign: 'center',
          color: 'rgba(245,245,247,0.5)',
          fontSize: '17px',
          lineHeight: 1.8,
          maxWidth: 360,
          mx: 'auto',
          mb: 10,
          letterSpacing: '-0.01em',
        }}>
          Open to new opportunities, collaborations, and interesting conversations.
        </Typography>
      </motion.div>

      {/* ── Email ─────────────────────────────────────────────────── */}
      <motion.div {...fadeUp(0.2)}>
        <Box sx={{
          textAlign: 'center',
          mb: 10,
          pb: 10,
          borderBottom: '1px solid rgba(255,255,255,0.07)',
        }}>
          <Typography sx={{
            fontSize: '0.7rem',
            color: 'rgba(245,245,247,0.3)',
            textTransform: 'uppercase',
            letterSpacing: '0.14em',
            mb: 2,
          }}>
            Email
          </Typography>
          <Box
            component="a"
            href="mailto:mike@zerotoil.com"
            onMouseEnter={() => setEmailHovered(true)}
            onMouseLeave={() => setEmailHovered(false)}
            sx={{
              display: 'inline-block',
              color: emailHovered ? '#2997FF' : '#F5F5F7',
              textDecoration: 'none',
              fontSize: 'clamp(1.3rem, 4vw, 1.9rem)',
              fontWeight: 500,
              letterSpacing: '-0.03em',
              transition: 'color 0.22s ease',
              lineHeight: 1,
            }}
          >
            mike@zerotoil.com
          </Box>
        </Box>
      </motion.div>

      {/* ── Social links ──────────────────────────────────────────── */}
      <motion.div {...fadeUp(0.28)}>
        <Typography sx={{
          fontSize: '0.7rem',
          color: 'rgba(245,245,247,0.28)',
          textTransform: 'uppercase',
          letterSpacing: '0.14em',
          mb: 1,
        }}>
          Elsewhere
        </Typography>
      </motion.div>

      <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
        {socials.map((item, i) => (
          <SocialRow key={item.label} item={item} index={i} />
        ))}
      </Box>

      {/* ── Footer ────────────────────────────────────────────────── */}
      <motion.div {...fadeUp(0.55)}>
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          gap: 5,
          flexWrap: 'wrap',
          mt: 10,
        }}>
          {[
            { Icon: LocationOnIcon, text: 'San Francisco Bay Area' },
            { Icon: AccessTimeIcon, text: 'Pacific Time (PT)' },
          ].map(({ Icon, text }) => (
            <Box key={text} sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
              <Icon sx={{ fontSize: '0.82rem', color: 'rgba(245,245,247,0.2)' }} />
              <Typography sx={{
                color: 'rgba(245,245,247,0.28)',
                fontSize: '0.82rem',
                letterSpacing: '-0.01em',
              }}>
                {text}
              </Typography>
            </Box>
          ))}
        </Box>
      </motion.div>

    </Container>
  );
}
